import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BottomNavComponent } from '../shell/bottom-nav/bottom-nav.component';
import { PlatformService } from '../services/platform.service';
import { SessionService } from '../services/session.service';

// Full-screen flows that should show no nav at all (camera/test capture,
// cognitive/sense modules, transient loader). B2 (focused-task mode): the full
// daily check-in is also a focused task - hide the bottom nav so it is not a
// competing exit; the check-in keeps its own top-left back/exit and one CTA per
// screen. (The symptom sweep hides the nav separately via body.sweep-active.)
// F4 (Danna QA 09.09): the intake questionnaire is an active task too, and it
// was the one funnel screen still showing the nav. "symptoms" here only matches
// /app/symptoms/<id>; /app/symptoms-form (the sweep) is followed by "-", not "/"
// or end of string, so it is unaffected and keeps using body.sweep-active.
const FULLSCREEN_RE =
  /\/app\/(cognitive|sense|test|loading|daily-checkin|symptoms)(\/|$)/;

@Component({
  selector: 'main',
  standalone: true,
  imports: [CommonModule, RouterOutlet, BottomNavComponent],
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss',
})
export class MainComponent implements OnDestroy {
  // The old web "Hub" hamburger header is GONE everywhere. The bottom/top nav
  // (BottomNavComponent — bottom bar on mobile, top bar on desktop) is the only
  // menu, shown on every authed in-app page (web AND native) except onboarding
  // (bare) and the full-screen capture flows.
  //   showNav  → render the nav + reserve clearance (broad: all authed pages).
  //   isShell  → the narrow 480px phone-shell layout (tool surfaces only, so
  //              wide funnel pages like results/payment keep their width).
  isShell = false;
  showNav = false;
  readonly isNativeAndroid: boolean;
  private sub: Subscription;
  private lastPath = '';
  @ViewChild('scroller') private scroller?: ElementRef<HTMLElement>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private platform: PlatformService,
    private session: SessionService,
  ) {
    // Android already keeps the WebView above the system navigation area in
    // MainActivity. Mark the shell so its CSS does not reserve the same inset a
    // second time. iOS remains edge-to-edge and still needs the CSS safe area.
    this.isNativeAndroid = this.platform.isNative && this.platform.isAndroid;
    this.sub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.updateShell();
        this.resetScrollOnNewPage();
      });
    this.updateShell();
  }

  // The app scrolls inside the section, not the window, so a new page would
  // open at the previous page's scroll offset (Danna 11.10: New scan opened
  // mid-screen). Reset on a path change only; query-param moves (results
  // ?panel=) keep their position.
  private resetScrollOnNewPage(): void {
    const path = this.router.url.split(/[?#]/)[0];
    if (path === this.lastPath) return;
    this.lastPath = path;
    const el = this.scroller?.nativeElement;
    if (el && el.scrollTop > 0) el.scrollTop = 0;
  }

  private updateShell(): void {
    let r = this.route;
    while (r.firstChild) r = r.firstChild;
    const data = r?.snapshot?.data || {};

    const native = this.platform.isNative;
    const authed = !!this.session.session?.token;
    // UX review 19.10 (3): the steps overview BETWEEN stages of a scan already
    // in progress (/app/new-test?scanId=...) is part of the active assessment,
    // so its nav goes too; it keeps its own header back. A fresh /app/new-test
    // (no scan yet, reached from the New scan tab) keeps the nav.
    const midScan = /\/app\/new-test(\?|$)/.test(this.router.url) &&
      /[?&]scanId=/.test(this.router.url);
    const fullscreen = FULLSCREEN_RE.test(this.router.url) || midScan;

    // The nav is the only menu — show it on every in-app page, authed OR the
    // public lead funnel (e.g. new-test), except onboarding (bare) and the
    // full-screen capture/loader flows. Tabs into authed areas stay protected
    // by the route guards (a logged-out tap lands on login).
    this.showNav = !data['bare'] && !fullscreen;

    // Narrow phone-shell layout: the dedicated tool surfaces (data.shell), plus
    // every authed page on native (the app is phone-width there anyway).
    const nativeShell = native && authed && !data['bare'] && !fullscreen;
    this.isShell = !!data['shell'] || nativeShell;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
